import React, { useEffect, useMemo, useRef, useState } from 'react';
import { sanitizeArtifactHtml } from '../pages/artifacts/safeHtmlRuntime';

const SUBMIT_MESSAGE = 'workflow86-artifact-submit';
const RESIZE_MESSAGE = 'workflow86-artifact-resize';
const LINK_OPEN_MESSAGE = 'workflow86-artifact-open-link';
const LINK_OPEN_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);

function createFrameId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function buildSrcDoc({ frameId, html, disabled }) {
  const safeHtml = sanitizeArtifactHtml(html || '');
  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      html, body { margin: 0; min-height: 100%; }
      body { box-sizing: border-box; }
      *, *::before, *::after { box-sizing: border-box; }
    </style>
  </head>
  <body>
    ${safeHtml}
    <script>
      (() => {
        const frameId = ${JSON.stringify(frameId)};
        const disabled = ${disabled ? 'true' : 'false'};
        const SUBMIT_MESSAGE = ${JSON.stringify(SUBMIT_MESSAGE)};
        const RESIZE_MESSAGE = ${JSON.stringify(RESIZE_MESSAGE)};
        const LINK_OPEN_MESSAGE = ${JSON.stringify(LINK_OPEN_MESSAGE)};

        function payloadFromForm(form) {
          const formData = new FormData(form);
          const payload = {};
          for (const [key, value] of formData.entries()) {
            if (!key || value instanceof Blob) continue;
            if (payload[key] === undefined) payload[key] = value;
            else if (Array.isArray(payload[key])) payload[key].push(value);
            else payload[key] = [payload[key], value];
          }
          return payload;
        }

        function postHeight() {
          const body = document.body;
          const doc = document.documentElement;
          parent.postMessage({
            type: RESIZE_MESSAGE,
            frameId,
            height: Math.max(body.scrollHeight, body.offsetHeight, doc.scrollHeight, doc.offsetHeight)
          }, '*');
        }

        function linkHrefFromTarget(target) {
          const anchor = target && target.closest ? target.closest('a[href]') : null;
          if (!anchor) return '';
          const targetAttr = (anchor.getAttribute('target') || '').toLowerCase();
          if (targetAttr !== '_blank') return '';
          return anchor.getAttribute('href') || '';
        }

        document.addEventListener('submit', event => {
          const form = event.target && event.target.closest ? event.target.closest('form') : null;
          if (!form) return;
          event.preventDefault();
          event.stopPropagation();
          if (disabled) return;
          parent.postMessage({
            type: SUBMIT_MESSAGE,
            frameId,
            eventName: (form.getAttribute('data-artifact-event') || '').trim(),
            payload: payloadFromForm(form)
          }, '*');
        }, true);

        document.addEventListener('click', event => {
          const href = linkHrefFromTarget(event.target);
          if (!href) return;
          event.preventDefault();
          event.stopPropagation();
          parent.postMessage({
            type: LINK_OPEN_MESSAGE,
            frameId,
            href
          }, '*');
        }, true);

        window.addEventListener('load', postHeight);
        if (typeof ResizeObserver !== 'undefined') {
          new ResizeObserver(postHeight).observe(document.body);
        }
        if (typeof MutationObserver !== 'undefined') {
          new MutationObserver(postHeight).observe(document.body, {
            attributes: true,
            childList: true,
            subtree: true
          });
        }
        setTimeout(postHeight, 0);
      })();
    </script>
  </body>
</html>`;
}

function safeArtifactLinkUrl(href) {
  if (typeof href !== 'string' || !href.trim()) return '';
  try {
    const url = new URL(href.trim(), window.location.origin);
    return LINK_OPEN_PROTOCOLS.has(url.protocol) ? url.href : '';
  } catch {
    return '';
  }
}

export default function ArtifactFrame({
  html,
  disabled = false,
  onSubmitEvent,
  className,
  title = 'Artifact',
  minHeight = 240,
  style,
}) {
  const iframeRef = useRef(null);
  const frameId = useMemo(createFrameId, []);
  const [height, setHeight] = useState(minHeight);
  const srcDoc = useMemo(
    () => buildSrcDoc({ frameId, html, disabled }),
    [disabled, frameId, html]
  );

  useEffect(() => {
    function handleMessage(event) {
      if (event.source !== iframeRef.current?.contentWindow) return;
      const message = event.data || {};
      if (message.frameId !== frameId) return;
      if (message.type === RESIZE_MESSAGE && Number.isFinite(message.height)) {
        setHeight(Math.max(minHeight, Math.ceil(message.height)));
      }
      if (message.type === SUBMIT_MESSAGE) {
        onSubmitEvent?.(message.eventName || '', message.payload || {});
      }
      if (message.type === LINK_OPEN_MESSAGE) {
        const href = safeArtifactLinkUrl(message.href);
        if (href) window.open(href, '_blank', 'noopener,noreferrer');
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [frameId, minHeight, onSubmitEvent]);

  return (
    <div className={className} style={style}>
      <iframe
        ref={iframeRef}
        title={title}
        srcDoc={srcDoc}
        sandbox="allow-scripts"
        style={{
          border: 0,
          display: 'block',
          height,
          minHeight,
          width: '100%',
        }}
      />
    </div>
  );
}
